import { z } from 'zod';

/**
 * The minimal projection of an emitted voucher that the `trip_update` e-mail prints as its PDF
 * (the content of the legacy voucher). The conversation parses the raw `voucher.finished` through
 * it (zod strip: whatever the PDF does not read stays out) and puts the result on the outbound
 * notification; the email-integration renders it. Every field but `voucherCode`/`modal` is
 * optional: a missing field drops its line, never the PDF. PII: never logged.
 *
 * Dates arrive as an ISO string or Mongo extended JSON (`{"$date": …}`) and leave as ISO.
 */
/** ISO string | `{$date: ISO}` | Date → ISO string. */
const unwrapDate = (v: unknown): unknown => {
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'object' && v !== null && '$date' in v) {
    const inner: unknown = v.$date;
    return inner instanceof Date ? inner.toISOString() : inner;
  }
  return v;
};

/** Optional wire date: a non-empty string after the unwrap, anything else reads as absent. */
const wireDate = z.preprocess((v) => {
  const unwrapped = unwrapDate(v);
  return typeof unwrapped === 'string' && unwrapped !== '' ? unwrapped : null;
}, z.string().nullish());

/** Optional number, also as a numeric string ("10500"); anything else reads as absent. */
const looseNumber = z.preprocess((v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}, z.number().nullish());

const namedSchema = z.object({ name: z.string().nullish() });

/** Only the name: the PDF prints no e-mail. */
const personSchema = z.object({ name: z.string().nullish() });

const stopSchema = z.object({
  name: z.string().nullish(),
  address: z.string().nullish(),
});

const stopoverSchema = z.object({
  flightNumber: z.string().nullish(),
  departingCity: z.string().nullish(),
  arrivalCity: z.string().nullish(),
  departingDate: wireDate,
  arrivalDate: wireDate,
  ciaOperational: namedSchema.nullish(),
  layover: z
    .object({
      changeAirport: z.boolean().nullish(),
      changeAirplane: z.boolean().nullish(),
      waitSecondsUntilNextFlight: looseNumber,
      airportConnection: z.string().nullish(),
      airplaneNumber: z.string().nullish(),
    })
    .nullish(),
});

const flightLegSchema = z.object({
  locator: z.string().nullish(),
  companyLocator: z.string().nullish(),
  departingCity: z.string().nullish(),
  arrivalCity: z.string().nullish(),
  departingDate: wireDate,
  arrivalDate: wireDate,
  cia: z.string().nullish(),
  codeCia: z.string().nullish(),
  flightNumber: z.array(z.string()).nullish(),
  fares: z
    .object({
      tariffDetails: z
        // `description` traz a franquia REAL em prosa ("…incluído na tarifa: 1
        // peça."): é ela que substituiu o peso constante do template (#3305).
        .array(
          z.object({
            name: z.string().nullish(),
            description: z.string().nullish(),
            availability: z.union([z.boolean(), z.string()]).nullish(),
          }),
        )
        .nullish(),
    })
    .nullish(),
  stopovers: z.array(stopoverSchema).nullish(),
});

const busTravelSchema = z.object({
  description: z.string().nullish(),
  period: z.string().nullish(),
  travelDuration: looseNumber,
  travelSeat: z.string().nullish(),
  seatClassDescription: z.string().nullish(),
  travelInsurance: z.boolean().nullish(),
  travelCompany: namedSchema.nullish(),
  departure: z.object({ stop: stopSchema.nullish(), dateTime: wireDate }).nullish(),
  arrival: z.object({ stop: stopSchema.nullish(), dateTime: wireDate }).nullish(),
  connection: z
    .object({
      stop: stopSchema.nullish(),
      company: namedSchema.nullish(),
      seatClassDescription: z.string().nullish(),
      firstLegDuration: looseNumber,
      secondLegDuration: looseNumber,
      waitingInformation: z
        .object({
          departure: z.object({ dateTime: wireDate }).nullish(),
        })
        .nullish(),
    })
    .nullish(),
  connectionSeat: z.string().nullish(),
});

const hotelSchema = z.object({
  name: z.string().nullish(),
  address: z.string().nullish(),
  numNights: looseNumber,
  guests: looseNumber,
  roomDescription: z.string().nullish(),
  checkinDate: wireDate,
  checkoutDate: wireDate,
  checkinUrl: z.string().nullish(),
  checkinInstructions: z.string().nullish(),
  hasBreakFast: z.boolean().nullish(),
  isRefundable: z.boolean().nullish(),
  moreInformation: z.string().nullish(),
  companions: z.array(namedSchema).nullish(),
  payAtDestination: z.object({ hasFees: z.boolean().nullish() }).nullish(),
  cancelationPolicy: z.object({ startDate: wireDate }).nullish(),
});

const rentalPlaceSchema = z.object({ address: z.string().nullish(), date: wireDate });

/**
 * The fields the voucher PDF reads off a raw `voucher.finished`. `voucherCode` and `modal` are
 * required: without them there is neither a file name nor a template.
 */
export const VoucherPdfSourceSchema = z.object({
  voucherCode: z.string().min(1),
  modal: z.string().min(1),
  reservationNumber: z.string().nullish(),
  providerIdentifier: z.string().nullish(),
  requestedAt: wireDate,
  emittedAt: wireDate,
  tags: z.array(z.string()).nullish(),
  requester: personSchema.nullish(),
  traveler: personSchema.extend({ companions: z.array(namedSchema).nullish() }).nullish(),
  approval: z
    .object({
      obs: z.string().nullish(),
      company: namedSchema.nullish(),
      costCenter: namedSchema.nullish(),
    })
    .nullish(),
  pricing: z
    .object({
      amount: looseNumber,
      currency: z.string().nullish(),
    })
    .nullish(),
  journey: z
    .object({
      origin: z.string().nullish(),
      destiny: z.string().nullish(),
      dateStart: wireDate,
      dateEnd: wireDate,
    })
    .nullish(),
  flight: z.array(flightLegSchema).nullish(),
  conciergeFlight: z.array(flightLegSchema).nullish(),
  hotel: hotelSchema.nullish(),
  conciergeHotel: hotelSchema.nullish(),
  rental: z
    .object({
      agency: stopSchema.extend({ agencyName: z.string().nullish() }).nullish(),
      agencyDropoff: stopSchema.nullish(),
      places: z
        .object({ pickup: rentalPlaceSchema.nullish(), dropoff: rentalPlaceSchema.nullish() })
        .nullish(),
      car: z.object({ name: z.string().nullish(), group: z.string().nullish() }).nullish(),
    })
    .nullish(),
  bus: busTravelSchema.nullish(),
});

export type VoucherPdfSource = z.infer<typeof VoucherPdfSourceSchema>;
export type VoucherPdfFlightLeg = z.infer<typeof flightLegSchema>;
export type VoucherPdfBusTravel = z.infer<typeof busTravelSchema>;
export type VoucherPdfHotel = z.infer<typeof hotelSchema>;

/** Most voucher PDFs one notification carries. */
export const MAX_VOUCHER_PDFS = 10;

/**
 * The list as the wire carries it, read leniently: an entry that is not a voucher is left out and
 * the rest still print, so a drifted entry costs its own PDF and never the e-mail.
 */
export const VoucherPdfListSchema = z.unknown().transform((value) => {
  if (!Array.isArray(value)) return undefined;
  const pdfs = value.slice(0, MAX_VOUCHER_PDFS).flatMap((entry: unknown) => {
    const parsed = VoucherPdfSourceSchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
  return pdfs.length > 0 ? pdfs : undefined;
});
